// @ts-nocheck
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps } from "@store/Library/Components/Toolkit/Block";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import ChevronLeft from "@store/Library/Components/Toolkit/Icons/ChevronLeft";
import ChevronRight from "@store/Library/Components/Toolkit/Icons/ChevronRight";
import ChevronUp from "@store/Library/Components/Toolkit/Icons/ChevronUp";
import { carouselDotWrapperStyles } from "@store/Themes/common/mixins/carousel";
import { transitionControlStyles, transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { buttonResetProps } from "@store/Themes/common/props/button";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

export type CarouselArrowDirection = "up" | "right" | "down" | "left";

export const carouselGetArrowSrc = (direction: CarouselArrowDirection) => {
    switch (direction) {
        case "up":
            return ChevronUp;
        case "right":
            return ChevronRight;
        case "down":
            return ChevronDown;
        case "left":
            return ChevronLeft;
        default:
            return "";
    }
};

export const carouselGetArrowProps = (
    direction: CarouselArrowDirection,
    size = pxToRem(32),
): ButtonPresentationProps => ({
    ...buttonResetProps,
    icon: {
        src: carouselGetArrowSrc(direction),
        position: "left",
    },
    css: css`
        ${buttonResetProps.css}
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size};
        width: 1em;
        height: 1em;
        border-radius: 50%;
        box-shadow: ${x => x.theme.shadows.e40};
        background-color: ${getColor("common.background")};
        transition: ${transitionHoverStyles("color")};

        &:not(:disabled) {
            color: ${getColor("primary")};

            &:hover,
            &:active {
                color: ${getColor("primary.dark")};
            }
        }

        ${IconWrapper} {
            ${iconScalableStyles("0.9375em")}
            top: 0;
            margin: 0;
        }
    `,
});

export const carouselGetDotProps = (selected?: boolean, color = "inherit"): ButtonPresentationProps => ({
    ...buttonResetProps,
    css: css`
        ${buttonResetProps.css}
        color: ${getColor(color)};
        background-color: currentColor;
        width: ${pxToRem(8)};
        height: ${pxToRem(8)};
        margin-left: ${pxToRem(8)};
        border-radius: 50%;
        transition: ${transitionControlStyles("color", "opacity")};
        ${!selected ? "opacity: 0.3;" : "cursor: default;"}

        &:first-child {
            margin-left: 0;
        }

        &:hover,
        &:active {
            color: ${getColor(color)};
            background-color: currentColor;
            opacity: 1;
        }
    `,
});

export const getArrowWrapperProps = (direction: "right" | "left"): InjectableCss => ({
    css: css`
        position: absolute;
        top: 50%;
        ${direction}: 0;
        z-index: 1;
        ${breakpointValues(
            [
                `translate3d(${direction === "left" ? "-" : ""}50%, -50%, 0)`,
                `translate3d(${direction === "left" ? "-" : ""}60%, -50%, 0)`,
            ],
            ["transform"],
        )}
    `,
});

export interface CarouselProps {
    titleText?: TypographyPresentationProps;
    block?: BlockProps;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

export const carouselProps: CarouselProps = {
    titleText: {
        variant: "h2",
    },
    prevArrowWrapper: getArrowWrapperProps("left"),
    prevArrowButton: carouselGetArrowProps("left"),
    nextArrowWrapper: getArrowWrapperProps("right"),
    nextArrowButton: carouselGetArrowProps("right"),
    carouselWrapper: {
        css: css`
            display: flex;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            margin: ${pxToRem(0, -15)};
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            padding: ${pxToRem(0, 15)};
        `,
    },
    carouselSlideInner: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
        `,
    },
    dotsWrapper: {
        css: css`
            ${carouselDotWrapperStyles()}
            ${spacingStyles(pxToRem(30), null)}
        `,
    },
    dotButton: carouselGetDotProps(),
    dotButtonSelected: carouselGetDotProps(true),
};
